import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, switchMap, timeout } from 'rxjs';
import { ContractDetail } from '../models/contract-model';
import { PaymentMinMaxBoundary } from '../payment/models/payment-data.model';
import { AppMediatorService } from './app-mediator.service';
import { ContractService } from './contract.service';
import { FeatureToggleService } from './feature-toggle.service';
import { LoggerService } from './logger.service';

// Bounds the live eSign read so a hung provider call cannot stall the serialized detail-load recheck.
const esignStateTimeoutMs = 10_000;

export type QuoteStepName = 'payment' | 'eSign';
export type QuoteStepState = 'complete' | 'incomplete' | 'notRequired' | 'pending';

export interface QuoteStepInputs {
    paymentRequired: boolean;
    eSignRequired: boolean;
    // null => boundary not yet resolved; Infinity/positive => not met; <=0 => met (matches
    // contract-detail.component.ts's paymentMinimum convention: getPaymentStepStatus/ngOnInit boundary subscription).
    paymentMinimum: number | null;
    eSignUrl?: string;
    eSignState?: string;
}

export interface QuoteStepStatus {
    payment: QuoteStepState;
    eSign: QuoteStepState;
    hasAnyRequiredStep: boolean;
    // vacuously true when no step is required
    allRequiredStepsComplete: boolean;
    // AC5 order: payment, then eSign
    firstIncompleteRequiredStep: QuoteStepName | null;
    shouldAutoConvert: boolean;
}

// Identical rule to contract-detail.component.ts's getPaymentStepStatus (:213-218):
// unresolved boundary => 'pending'; minAmount <= 0 => met.
export const isPaymentStepComplete = (paymentMinimum: number | null): boolean => paymentMinimum !== null && paymentMinimum <= 0;

// Button-visibility only (contract-detail.component.ts's isEsignAvailable()): a step is pending
// while EsignUrl is set and ESignState is falsy or 'Requested'. Permissive on unknown states, since
// the worst case here is just an unnecessary Sign button.
export const isESignPending = (eSignUrl?: string, eSignState?: string): boolean => !!eSignUrl && (!eSignState || eSignState === 'Requested');

// Auto-conversion gate only: an allowlist, since this now decides an irreversible Quote -> Reservation
// transition. Unlike isESignPending, an unknown ESignState (or a missing EsignUrl) must NOT read as
// signed - only an explicit 'Signed' state does.
const signedStates = ['Signed'];
export const isESignStepComplete = (eSignUrl?: string, eSignState?: string): boolean => !!eSignState && signedStates.includes(eSignState);

export function getQuoteStepStatus(inputs: QuoteStepInputs): QuoteStepStatus {
    const payment: QuoteStepState = !inputs.paymentRequired ? 'notRequired' : inputs.paymentMinimum === null ? 'pending' : isPaymentStepComplete(inputs.paymentMinimum) ? 'complete' : 'incomplete';

    const eSign: QuoteStepState = !inputs.eSignRequired ? 'notRequired' : isESignStepComplete(inputs.eSignUrl, inputs.eSignState) ? 'complete' : 'incomplete';

    const hasAnyRequiredStep = inputs.paymentRequired || inputs.eSignRequired;
    const allRequiredStepsComplete = (payment === 'complete' || payment === 'notRequired') && (eSign === 'complete' || eSign === 'notRequired');
    const firstIncompleteRequiredStep: QuoteStepName | null = payment === 'incomplete' || payment === 'pending' ? 'payment' : eSign === 'incomplete' ? 'eSign' : null;

    return {
        payment,
        eSign,
        hasAnyRequiredStep,
        allRequiredStepsComplete,
        firstIncompleteRequiredStep,
        shouldAutoConvert: hasAnyRequiredStep && allRequiredStepsComplete
    };
}

@Injectable({ providedIn: 'root' })
export class QuoteStepService {
    private readonly featureToggleService = inject(FeatureToggleService);
    private readonly appMediatorService = inject(AppMediatorService);
    private readonly contractService = inject(ContractService);
    private readonly logger = inject(LoggerService);

    /**
     * Effects entry point: resolves the live eSign state (when an eSign is required and requested) and the
     * payment boundary (only when Payment is required, matching the "skip the extra boundary fetch otherwise"
     * convention already used in payment.effects.ts) then applies the shared step rules. UI code that already
     * has `paymentMinimum` loaded can call `getQuoteStepStatus` directly instead.
     *
     * eSign state is read from the provider (`getEsignState`), never from `contractDetail.ESignState`: the stored
     * value is only refreshed by that same call, and this result gates an irreversible Quote -> Reservation transition.
     *
     * `previousMinAmount` is for the post-payment caller only (ADO-111669): the pre-payment minimum, or
     * null when none was known. When supplied, a single boundary read can still return that stale pre-payment
     * minimum, so a previously-met minimum (<= 0) short-circuits without a fetch (a payment only adds money)
     * and anything else polls via getMinMaxFromPaymentBoundaryUntilChanged. Omitted => the plain single read.
     */
    resolveStepStatus(customerId: string, contractId: string, contractDetail: ContractDetail | null | undefined, previousMinAmount?: number | null): Observable<QuoteStepStatus> {
        const eSignRequired = this.featureToggleService.isAvailable('eSignRequired');
        const eSignState$ = eSignRequired && contractDetail?.EsignUrl ? this.fetchLiveEsignState(customerId, contractId) : of(contractDetail?.ESignState);
        return eSignState$.pipe(switchMap(eSignState => this.resolveWithEsignState(customerId, contractId, contractDetail, eSignState, previousMinAmount)));
    }

    // Fails closed: an unreadable provider state must read as "not signed", never fall back to the stored value.
    private fetchLiveEsignState(customerId: string, contractId: string): Observable<string | undefined> {
        return this.contractService.getEsignState(customerId, contractId).pipe(
            timeout(esignStateTimeoutMs),
            map(response => response?.EsignState),
            catchError(error => {
                this.logger.logError(
                    JSON.stringify({ context: 'QuoteStepService.resolveStepStatus', message: 'eSign state fetch failed', customerId, contractId, error: error?.message, status: error?.status }),
                    '',
                    true
                );
                return of(undefined);
            })
        );
    }

    private resolveWithEsignState(
        customerId: string,
        contractId: string,
        contractDetail: ContractDetail | null | undefined,
        eSignState: string | undefined,
        previousMinAmount?: number | null
    ): Observable<QuoteStepStatus> {
        const paymentRequired = this.featureToggleService.isAvailable('paymentRequired');
        const eSignRequired = this.featureToggleService.isAvailable('eSignRequired');
        const base = { paymentRequired, eSignRequired, eSignUrl: contractDetail?.EsignUrl, eSignState };

        if (!paymentRequired) {
            return of(getQuoteStepStatus({ ...base, paymentMinimum: null }));
        }

        if (previousMinAmount !== undefined && previousMinAmount !== null && previousMinAmount <= 0) {
            return of(getQuoteStepStatus({ ...base, paymentMinimum: previousMinAmount }));
        }

        const boundaries$ =
            previousMinAmount === undefined
                ? this.appMediatorService.porPayService.getMinMaxFromPaymentBoundary(customerId, contractId)
                : this.appMediatorService.porPayService.getMinMaxFromPaymentBoundaryUntilChanged(customerId, contractId, previousMinAmount);

        return boundaries$.pipe(
            map((boundaries: PaymentMinMaxBoundary[]) =>
                getQuoteStepStatus({
                    ...base,
                    // A missing/empty boundary is NOT "met" — same convention as payment.effects.ts / contract-detail.component.ts.
                    paymentMinimum: boundaries?.length ? Math.min(...boundaries.map(b => b.minAmount)) : Infinity
                })
            ),
            catchError(error => {
                // A silent fallback here is indistinguishable from a legitimately unmet minimum, but it
                // changes auto-conversion behaviour org-wide - surface it so an outage (e.g. the boundary
                // endpoint going down) can be correlated against "auto-conversion mysteriously stopped".
                this.logger.logError(
                    JSON.stringify({ context: 'QuoteStepService.resolveStepStatus', message: 'Payment boundary fetch failed', customerId, contractId, error: error?.message, status: error?.status }),
                    '',
                    true
                );
                return of(getQuoteStepStatus({ ...base, paymentMinimum: Infinity }));
            })
        );
    }
}
